import React, { ReactNode,useEffect, useRef, useState } from 'react'
import { usePopper } from 'react-popper'

import { H3,Tag } from '@driftt/tide-core'
import { Icon } from '@driftt/tide-icons'
import { access } from '@driftt/traffic-control'

import { CustomerApi } from 'api'
import Portal from 'components/Portal'
import useOnClickOutside from 'hooks/useOnClickOutside'
import { captureException } from 'utils/errors'
import { salesforceIntegrationUrl } from 'utils/paths'

import styles from './styles.module.css'

enum SyncType {
  SYNC_ALL_ACCOUNTS = 'SYNC_ALL_ACCOUNTS',
  SYNC_SPECIFIC_ACCOUNT_LISTS = 'SYNC_SPECIFIC_ACCOUNT_LISTS',
  DONT_SYNC_ACCOUNTS = 'DONT_SYNC_ACCOUNTS'
}

interface SyncStatusProps {
  children: ReactNode
  checked: boolean
}

interface AccountSyncInfoCardProps {
  ref: any
  attributes: Record<string, any>
  popperStyles: Record<string, any>
  mappedFields: any
  mappedUsers: any
  syncType?: SyncType
  onClose: () => void
}

const SyncStatus: React.FC<SyncStatusProps> = ({ children, checked }) => {
  return (
    <div className={styles.syncStatus}>
      <div className={styles.syncIcon}>
        <Icon name={checked ? 'check' : 'close'} width="14px" />
      </div>
      <div>{children}</div>
    </div>
  )
}

const AccountSyncInfoCard: React.FC<AccountSyncInfoCardProps> = React.forwardRef((props, ref) => {
  const cardRef = useRef(null)
  const { attributes, popperStyles, mappedFields, mappedUsers, syncType } = props
  const isOrgAdmin = access.hasScopes('org_admin')

  useOnClickOutside(cardRef, props.onClose)

  return (
    <div ref={ref} className={styles.popper} style={popperStyles.popper} {...attributes.popper}>
      <div ref={cardRef} className={styles.cardStyle}>
        <div className={styles.cardHeader}>
          <H3 className={styles.syncTitle}>Integration details</H3>
          {isOrgAdmin && (
            <a
              className={styles.syncUrl}
              href={salesforceIntegrationUrl}
              target="_blank"
              rel="noopener noreferrer">
              Integration settings
              <Icon height={16} width={16} name="open-new-tab" className={styles.openTabIcon} />
            </a>
          )}
        </div>
        {syncType && (
          <SyncStatus checked={syncType !== SyncType.DONT_SYNC_ACCOUNTS}>
            {syncType === SyncType.SYNC_ALL_ACCOUNTS && (
              <span>
                <b>All accounts</b> synced
              </span>
            )}
            {syncType === SyncType.SYNC_SPECIFIC_ACCOUNT_LISTS && (
              <span>
                <b>Selected accounts</b> synced
              </span>
            )}
            {syncType === SyncType.DONT_SYNC_ACCOUNTS && (
              <span>
                <b>No accounts</b> synced
              </span>
            )}
          </SyncStatus>
        )}
        <SyncStatus checked={mappedFields.length > 0}>
          <b>
            {mappedFields.length} account {mappedFields.length === 1 ? 'field' : 'fields'}
          </b>{' '}
          mapped
        </SyncStatus>
        <SyncStatus checked={mappedUsers.length > 0}>
          <b>
            {mappedUsers.length} {mappedUsers.length === 1 ? 'user' : 'users'}
          </b>{' '}
          mapped
        </SyncStatus>
        {!isOrgAdmin && (
          <React.Fragment>
            <hr className={styles.breakLine} />
            <div className={styles.contactAdminPrompt}>
              Contact your Drift admin to edit the accounts, fields, and users synced from
              Salesforce
            </div>
          </React.Fragment>
        )}
      </div>
    </div>
  )
})

const AccountSyncFromSalesforcePanel = () => {
  const [isSyncPanelOpen, setIsSyncPanelOpen] = useState(false)
  const [referenceElement, setReferenceElement] = useState<HTMLElement | null>(null)
  const [popperElement, setPopperElement] = useState<HTMLElement | null>(null)
  const [mappedFields, setMappedFields] = useState([])
  const [mappedUsers, setMappedUsers] = useState([])
  const [syncType, setSyncType] = useState()

  const { styles: popperStyles, attributes } = usePopper(referenceElement, popperElement, {
    placement: 'bottom',
    modifiers: [
      {
        name: 'offset',
        options: {
          offset: [-60, 5]
        }
      }
    ]
  })

  useEffect(() => {
    const fetchAttributeMappings = async () => {
      try {
        const { data } = await CustomerApi.get('/salesforce/mappings/attributes')
        setMappedFields(data?.mappings || [])
      } catch (err) {
        captureException(err)
      }
    }
    fetchAttributeMappings()
  }, [])

  useEffect(() => {
    const fetchUserMappings = async () => {
      try {
        const { data } = await CustomerApi.get('/salesforce/mappings/users/overrides')
        setMappedUsers(data?.mappings || [])
      } catch (err) {
        captureException(err)
      }
    }
    fetchUserMappings()
  }, [])

  useEffect(() => {
    const fetchAccountSyncType = async () => {
      try {
        const { data } = await CustomerApi.get('/salesforce/toggles/accountsync')
        setSyncType(data)
      } catch (err) {
        captureException(err)
      }
    }
    fetchAccountSyncType()
  }, [])

  return (
    <React.Fragment>
      <div
        ref={setReferenceElement}
        className={styles.syncElement}
        onClick={() => setIsSyncPanelOpen(!isSyncPanelOpen)}>
        <Tag
          wrapperClass={styles.syncBadge}
          textClass={styles.syncBadgeText}
          text="Salesforce Connected"
        />
        <Icon name="chevron-down" width={12} />
      </div>
      <Portal>
        {isSyncPanelOpen && (
          <AccountSyncInfoCard
            ref={setPopperElement}
            attributes={attributes}
            popperStyles={popperStyles}
            mappedFields={mappedFields}
            mappedUsers={mappedUsers}
            syncType={syncType}
            onClose={() => setIsSyncPanelOpen(false)}
          />
        )}
      </Portal>
    </React.Fragment>
  )
}

export default AccountSyncFromSalesforcePanel
